import { API_CONFIG } from "../config/api";

export interface ChatMessage {
  id: string;
  session_id: string;
  user_id: string;
  message: string;
  source: "user" | "ai";
  created_at: string;
  updated_at: string;
}

export interface ChatSession {
  session_id: string;
  user_id?: string;
  title: string;
  created_at: string;
  updated_at?: string;
  first_message?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  platform?: string;
}

export interface User {
  id: string;
  name: string;
  cpf: string;
  email: string;
  verifiedEmail: boolean;
  gmail: string;
  verifiedGmail: boolean;
  phone: string;
  verifiedPhone: boolean;
  role: string;
  active: boolean;
  doctorProfile: Record<string, any>;
  preferences: Record<string, any>;
  created_at: string;
  updated_at: string;
  extra_info: Record<string, any>;
  first_login: boolean;
  deactivated_at: string | null;
  deactivation_reason: string | null;
}

export type PlanKind = "monthly" | "annual" | "lifetime" | "trial";
export type AnnualBilling = "upfront" | "installments_12";

export interface PendingPlanChange {
  targetPlanKind: string;
  targetCode: string;
  effectiveAt: string;
}

export interface SubscriptionCommitment {
  total: number;
  paid: number;
  endsAt: string | null;
}

export interface SubscriptionInfo {
  status: "active" | "pending" | "canceled" | "failed";
  paymentUrl?: string | null;
  isLifetime?: boolean;
  planKind?: PlanKind;
  annualBilling?: AnnualBilling | null;
  isTrial?: boolean;
  trialEndsAt?: string | null;
  accessUntil?: string | null;
  pendingPlanChange?: PendingPlanChange | null;
  cancelScheduledAt?: string | null;
  cardLastDigits?: string | null;
  cardBrand?: string | null;
  commitment?: SubscriptionCommitment | null;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  firstLogin: boolean;
  needsTermsAcceptance: boolean;
  subscription: SubscriptionInfo | null;
}

export interface TermsResponse {
  id: string;
  version: string;
  content: string;
  createdAt: string;
  accepted: boolean;
}

export interface SubscriptionResponse {
  id: string;
  subscriptionCode: string;
  planKind?: PlanKind;
  annualBilling?: AnnualBilling | null;
  status: string;
  amount: number;
  interval: string;
  intervalCount: number;
  currentCycleStart: string | null;
  currentCycleEnd: string | null;
  nextBillingAt: string | null;
  canceledAt: string | null;
  createdAt: string;
  isLifetime?: boolean;
  isTrial?: boolean;
  trialEndsAt?: string | null;
  accessUntil?: string | null;
  pendingPlanChange?: PendingPlanChange | null;
  cancelScheduledAt?: string | null;
  cardLastDigits?: string | null;
  cardBrand?: string | null;
  commitment?: SubscriptionCommitment | null;
}

export interface CreateSubscriptionResponse {
  subscriptionId: string;
  pagarmeSubscriptionId?: string;
  pagarmeOrderId?: string;
  paymentUrl?: string | null;
  status: string;
  pix?: {
    qrCode: string;
    qrCodeUrl: string;
    expiresAt: string;
  };
}

export interface InstallmentOption {
  number: number;
  total: number;
  installmentAmount: number;
  hasFee: boolean;
}

export interface InstallmentsResponse {
  baseAmount: number;
  installments: InstallmentOption[];
}

export interface SubscriptionPricingResponse {
  /** false = venda de novas assinaturas fechada (WEEDY_NEW_SALES_CLOSED no backend). */
  salesOpen?: boolean;
  monthly: {
    code: string;
    amount: number;
  };
  annual?: {
    upfront: { code: string; amount: number };
    installments12: {
      code: string;
      perCycle: number;
      count: number;
      total: number;
    };
  };
  lifetime: {
    code: string;
    baseAmount: number;
    installments: InstallmentOption[];
  };
  trial?: {
    days: number;
  };
}

export interface GetUserResponse {
  user: User;
}

export interface SendMessageRequest {
  sessionId?: string;
  message: string;
}

export interface SendMessageResponse {
  success: boolean;
  data: {
    sessionId: string;
    userMessage: string;
    aiMessage: string;
    session: ChatSession;
  };
}

export interface StreamEvent {
  type:
    | "session_info"
    | "token"
    | "done"
    | "complete"
    | "error"
    | "route_info"
    | "progress"
    | "reasoning";
  sessionId?: string;
  userMessage?: string;
  token?: string;
  fullMessage?: string;
  metadata?: any;
  processingTimeMs?: number;
  aiMessage?: string;
  error?: string;
  route?: "normal" | "agents" | "followup";
  agentsCalled?: string[];
  missingInfo?: string[];
  // "progress": status real de um agent especializado concluído (ver
  // ChatAgentRouter.call_agents_streaming no weed-doctor-ai-inference).
  agent?: string;
  status?: "ok" | "warn";
  // "reasoning": o que o sistema já decidiu enquanto os agentes não voltam.
  // Processamento visível, NÃO é resposta — o la-plata nunca o persiste como
  // mensagem. Chega em linhas curtas, uma por evento.
  text?: string;
}

export interface SessionsResponse {
  success: boolean;
  data: ChatSession[];
}

export interface SessionHistoryResponse {
  success: boolean;
  data: {
    session: ChatSession;
    messages: ChatMessage[];
  };
}

// Helper function to decode JWT token
function decodeJWT(
  token: string,
): { email: string; id: string; iat: number; exp: number } | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
}

class ApiService {
  private baseURL: string;
  private timeout: number;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    _retry: boolean = false,
  ): Promise<T> {
    const config: RequestInit = {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        ...options.headers,
      },
      signal: AbortSignal.timeout(this.timeout),
    };

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);

      const rawBody = await response.text();
      let parsedBody: any = null;
      try {
        parsedBody = rawBody ? JSON.parse(rawBody) : null;
      } catch {
        parsedBody = null;
      }

      // Só tenta refresh em TOKEN_EXPIRED (havia sessão, expirou). checkAuth
      // roda em todo mount agora (sem cookie legível para checar antes) — um
      // visitante nunca-logado bate aqui com TOKEN_MISSING/TOKEN_INVALID, que
      // não deve disparar refresh (não há nada para renovar).
      if (response.status === 401 && parsedBody?.code === "TOKEN_EXPIRED") {
        const refreshSuccess = await this.refreshAccessToken();
        if (refreshSuccess && !_retry) {
          return this.request(endpoint, options, true);
        }
      }

      if (!response.ok) {
        // 403 = assinatura/trial pode ter expirado no backend → avisa o
        // AuthContext para re-buscar a assinatura e os guards redirecionarem.
        if (response.status === 403) {
          window.dispatchEvent(new Event("subscription:refresh"));
        }
        const errorMessage =
          parsedBody?.errors?.[0] ||
          parsedBody?.message ||
          `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      return parsedBody as T;
    } catch (error) {
      if (error instanceof Error && error.name === "TimeoutError") {
        throw new Error(
          "Request timed out - AI response may still be processing",
        );
      }
      throw error;
    }
  }

  private async pollWithFibonacci(
    sessionId: string,
    initialMessageCount: number,
  ): Promise<ChatMessage[] | null> {
    const intervals = API_CONFIG.FIBONACCI_INTERVALS;
    const tailInterval = intervals[intervals.length - 1];
    const deadline = Date.now() + API_CONFIG.MAX_POLL_DURATION_MS;

    // Multi-agent responses can legitimately take longer than the fixed
    // fibonacci ramp-up, so once it's exhausted keep retrying at the last
    // interval until MAX_POLL_DURATION_MS instead of giving up at ~20s.
    for (let attempt = 0; Date.now() < deadline; attempt++) {
      const wait = intervals[attempt] ?? tailInterval;
      try {
        console.log(`Polling attempt ${attempt + 1} - waiting ${wait}ms`);

        await new Promise((resolve) => setTimeout(resolve, wait));

        const response = await this.getSessionHistory(sessionId);
        const messages = response.data.messages;

        // Check if new messages were added (AI responded)
        if (messages.length > initialMessageCount) {
          console.log(`Found new messages after ${attempt + 1} attempts`);
          return messages;
        }

        console.log(`No new messages found on attempt ${attempt + 1}`);
      } catch (error) {
        console.warn(`Polling attempt ${attempt + 1} failed:`, error);
      }
    }

    return null; // Exceeded MAX_POLL_DURATION_MS without a new message
  }

  // Public method to poll with fibonacci intervals
  async pollForResponse(
    sessionId: string,
    initialMessageCount: number,
  ): Promise<ChatMessage[] | null> {
    return this.pollWithFibonacci(sessionId, initialMessageCount);
  }

  // Stream message with Server-Sent Events
  async sendMessageStream(
    data: SendMessageRequest,
    onEvent: (event: StreamEvent) => void,
    _retry: boolean = false,
  ): Promise<void> {
    const response = await fetch(
      `${this.baseURL}${API_CONFIG.ENDPOINTS.CHAT_MESSAGE_STREAM}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(data),
      },
    );

    if (response.status === 401) {
      const refreshSuccess = await this.refreshAccessToken();
      if (refreshSuccess && !_retry) {
        return this.sendMessageStream(data, onEvent, true);
      }
    }

    if (!response.ok) {
      if (response.status === 403) {
        window.dispatchEvent(new Event("subscription:refresh"));
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.errors?.[0] ||
          errorData.message ||
          `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let dataTimeout: NodeJS.Timeout | null = null;
    let hasReceivedData = false;
    let streamCompleted = false;
    let streamStartTime = Date.now();

    const resetTimeout = () => {
      if (dataTimeout) {
        clearTimeout(dataTimeout);
      }

      dataTimeout = setTimeout(() => {
        if (!streamCompleted) {
          console.log(
            "[STREAM TIMEOUT] No data received for 20 seconds, forcing completion",
          );
          streamCompleted = true;
          if (dataTimeout) {
            clearTimeout(dataTimeout);
            dataTimeout = null;
          }
          onEvent({
            type: "complete",
            processingTimeMs: Date.now() - streamStartTime,
          });
        }
      }, 20000);
    };

    console.log("[STREAM START] Initiating SSE connection, timeout set to 20s");
    resetTimeout();

    try {
      while (true && !streamCompleted) {
        const { done, value } = await reader.read();

        if (done) {
          console.log("[STREAM DONE] Stream naturally completed");
          if (!streamCompleted) {
            streamCompleted = true;
            if (dataTimeout) {
              clearTimeout(dataTimeout);
              dataTimeout = null;
            }
            onEvent({
              type: "complete",
              processingTimeMs: Date.now() - streamStartTime,
            });
          }
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const eventData = JSON.parse(line.slice(6));

              if (eventData.type === "token") {
                hasReceivedData = true;
                resetTimeout();
              }

              if (eventData.type === "complete" || eventData.type === "done") {
                console.log(
                  `[STREAM EVENT] Received ${eventData.type} event from server`,
                );
                streamCompleted = true;
                if (dataTimeout) {
                  clearTimeout(dataTimeout);
                  dataTimeout = null;
                }
              }

              onEvent(eventData);

              // If we receive an error event, throw it
              if (eventData.type === "error") {
                streamCompleted = true;
                if (dataTimeout) {
                  clearTimeout(dataTimeout);
                  dataTimeout = null;
                }
                throw new Error(eventData.error || "Stream error");
              }
            } catch (parseError) {
              // Ignore invalid JSON lines but log them
              console.warn("Failed to parse SSE data:", line, parseError);
            }
          }
        }
      }
    } finally {
      if (dataTimeout) {
        clearTimeout(dataTimeout);
      }
      reader.releaseLock();
    }
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>(API_CONFIG.ENDPOINTS.LOGIN, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async logout(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(API_CONFIG.ENDPOINTS.LOGOUT, {
      method: "POST",
    });
  }

  async getUser(userId: string): Promise<GetUserResponse> {
    return this.request<GetUserResponse>(API_CONFIG.ENDPOINTS.GET_USER(userId));
  }

  async getMe(): Promise<GetUserResponse | null> {
    try {
      return await this.request<GetUserResponse>(API_CONFIG.ENDPOINTS.GET_ME);
    } catch {
      return null;
    }
  }

  // O backend só grava a especialidade: o conselho não se edita por aqui.
  async updateDoctorProfile(data: {
    speciality: string;
  }): Promise<{ message?: string }> {
    return this.request(API_CONFIG.ENDPOINTS.UPDATE_DOCTOR_PROFILE, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async updateDoctorPhone(data: {
    phone: string;
  }): Promise<{ message?: string }> {
    return this.request(API_CONFIG.ENDPOINTS.UPDATE_DOCTOR_PHONE, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async sendMessage(data: SendMessageRequest): Promise<SendMessageResponse> {
    try {
      const response = await this.request<SendMessageResponse>(
        API_CONFIG.ENDPOINTS.CHAT_MESSAGE,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
      );

      return response;
    } catch (error) {
      // If request timed out, we'll let the caller handle the polling logic
      // Don't try to recover here, just throw the error with context
      if (error instanceof Error && error.name === "TimeoutError") {
        throw new Error("timeout");
      }

      throw error;
    }
  }

  async getSessions(): Promise<SessionsResponse> {
    return this.request<SessionsResponse>(API_CONFIG.ENDPOINTS.CHAT_SESSIONS);
  }

  async getSessionHistory(sessionId: string): Promise<SessionHistoryResponse> {
    return this.request<SessionHistoryResponse>(
      API_CONFIG.ENDPOINTS.CHAT_HISTORY(sessionId),
    );
  }

  async deleteSession(
    sessionId: string,
  ): Promise<{ success: boolean; data: ChatSession }> {
    return this.request<{ success: boolean; data: ChatSession }>(
      API_CONFIG.ENDPOINTS.DELETE_SESSION(sessionId),
      { method: "DELETE" },
    );
  }

  async getTerms(): Promise<TermsResponse> {
    return this.request<TermsResponse>(API_CONFIG.ENDPOINTS.TERMS);
  }

  async acceptTerms(termsId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      API_CONFIG.ENDPOINTS.ACCEPT_TERMS,
      {
        method: "POST",
        body: JSON.stringify({ termsId }),
      },
    );
  }

  async getLifetimeLicenses(): Promise<{
    total: number;
    sold: number;
    available: number;
  }> {
    return this.request<{ total: number; sold: number; available: number }>(
      API_CONFIG.ENDPOINTS.LIFETIME_LICENSES,
    );
  }

  async getSubscription(): Promise<SubscriptionResponse> {
    return this.request<SubscriptionResponse>(
      API_CONFIG.ENDPOINTS.SUBSCRIPTION,
    );
  }

  async createSubscription(params: {
    cardToken: string;
    billingAddress: {
      line_1: string;
      zip_code: string;
      city: string;
      state: string;
    };
    planKind?: PlanKind;
    annualBilling?: AnnualBilling | null;
    startTrial?: boolean;
  }): Promise<CreateSubscriptionResponse> {
    return this.request<CreateSubscriptionResponse>(
      API_CONFIG.ENDPOINTS.SUBSCRIPTION,
      {
        method: "POST",
        body: JSON.stringify(params),
      },
    );
  }

  async cancelSubscription(): Promise<{
    subscriptionId: string;
    status: string;
    accessUntil?: string | null;
    scheduled?: boolean;
    message?: string;
  }> {
    return this.request(API_CONFIG.ENDPOINTS.SUBSCRIPTION, {
      method: "DELETE",
    });
  }

  async changePlan(params: {
    targetPlanKind: "monthly" | "annual";
    annualBilling?: AnnualBilling | null;
    cancelPending?: boolean;
  }): Promise<{
    subscriptionId: string;
    status?: string;
    planKind?: string;
    annualBilling?: string;
    prorationCredit?: number;
    pendingPlanChange?: PendingPlanChange | null;
    effectiveAt?: string;
    note?: string;
  }> {
    return this.request(API_CONFIG.ENDPOINTS.SUBSCRIPTION_CHANGE_PLAN, {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  async updateSubscriptionCard(params: { cardToken: string }): Promise<{
    subscriptionId: string;
    status: string;
    cardLastDigits?: string | null;
    cardBrand?: string | null;
  }> {
    return this.request(API_CONFIG.ENDPOINTS.SUBSCRIPTION_CARD, {
      method: "PATCH",
      body: JSON.stringify(params),
    });
  }

  async createLifetimeAccess(params: {
    paymentMethod: "credit_card" | "pix";
    cardToken?: string;
    installments?: number;
    billingAddress?: {
      line_1: string;
      zip_code: string;
      city: string;
      state: string;
    };
  }): Promise<CreateSubscriptionResponse> {
    return this.request<CreateSubscriptionResponse>(
      API_CONFIG.ENDPOINTS.LIFETIME_ACCESS,
      {
        method: "POST",
        body: JSON.stringify(params),
      },
    );
  }

  async getLifetimeInstallments(): Promise<InstallmentsResponse> {
    return this.request<InstallmentsResponse>(
      API_CONFIG.ENDPOINTS.LIFETIME_INSTALLMENTS,
    );
  }

  async getSubscriptionPricing(): Promise<SubscriptionPricingResponse> {
    return this.request<SubscriptionPricingResponse>(
      API_CONFIG.ENDPOINTS.SUBSCRIPTION_PRICING,
    );
  }

  async tokenizeCard(cardData: {
    number: string;
    holderName: string;
    expMonth: number;
    expYear: number;
    cvv: string;
  }): Promise<string> {
    const publicKey = API_CONFIG.PAGARME_PUBLIC_KEY;
    if (!publicKey)
      throw new Error("Chave pública do Pagar.me não configurada");

    const response = await fetch(
      `${API_CONFIG.PAGARME_API_URL}/tokens?appId=${publicKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "card",
          card: {
            number: cardData.number.replace(/\s/g, ""),
            holder_name: cardData.holderName,
            exp_month: cardData.expMonth,
            exp_year: cardData.expYear,
            cvv: cardData.cvv,
          },
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Erro ao tokenizar cartão");
    }

    const data = await response.json();
    return data.id;
  }

  async updatePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ message: string; first_login: boolean }> {
    return this.request<{ message: string; first_login: boolean }>(
      API_CONFIG.ENDPOINTS.UPDATE_PASSWORD,
      {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      },
    );
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      API_CONFIG.ENDPOINTS.FORGOT_PASSWORD,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
  }

  async verifyResetCode(
    email: string,
    code: string,
  ): Promise<{ resetToken: string }> {
    return this.request<{ resetToken: string }>(
      API_CONFIG.ENDPOINTS.VERIFY_RESET_CODE,
      {
        method: "POST",
        body: JSON.stringify({ email, code }),
      },
    );
  }

  async resetPassword(
    resetToken: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      API_CONFIG.ENDPOINTS.RESET_PASSWORD,
      {
        method: "POST",
        body: JSON.stringify({ resetToken, newPassword }),
      },
    );
  }

  async doctorSignup(data: {
    name: string;
    cpf: string;
    email: string;
    password: string;
    phone: string;
    councilNumber: string;
    councilType: string;
    speciality: string;
    billingAddress: {
      line_1: string;
      zip_code: string;
      city: string;
      state: string;
    };
    documentCommitmentAccepted: boolean;
  }): Promise<{ success: boolean; requiresEmailVerification: boolean }> {
    return this.request<{
      success: boolean;
      requiresEmailVerification: boolean;
    }>(API_CONFIG.ENDPOINTS.DOCTOR_SIGNUP, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async joinWaitlist(data: {
    name: string;
    email: string;
    phone: string;
    professionalType: string;
  }): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(API_CONFIG.ENDPOINTS.WAITLIST, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async verifyEmailSignup(
    email: string,
    code: string,
  ): Promise<{ verified: boolean; token?: string }> {
    return this.request<{ verified: boolean; token?: string }>(
      API_CONFIG.ENDPOINTS.VERIFY_EMAIL_SIGNUP,
      {
        method: "POST",
        body: JSON.stringify({ email, code }),
      },
    );
  }

  // Removem tokens que possam ter ficado de sessões anteriores à migração
  // para cookies httpOnly (SAL: proteção XSS) — o app não escreve mais essas
  // chaves, só limpa resíduos de clientes antigos.
  removeAuthToken() {
    localStorage.removeItem("authToken");
  }

  removeRefreshToken() {
    localStorage.removeItem("refreshToken");
  }

  clearTokenInfo(): void {
    this.removeAuthToken();
    this.removeRefreshToken();
    this.logout().catch(() => {});
    window.location.href = "/login";
  }

  async refreshAccessToken(): Promise<boolean> {
    try {
      await this.request<{ token: string }>(
        API_CONFIG.ENDPOINTS.REFRESH_ACCESS_TOKEN,
        { method: "POST" },
      );
      return true;
    } catch (error) {
      this.clearTokenInfo();
      console.error("Error trying to refresh token");
      return false;
    }
  }
}

export const apiService = new ApiService();
